import { CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useEffect, useMemo, useRef } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { EmptyTimelineState } from "web_myplann/NotificationCenter/Components/EmptyTimelineState";
import { MarkAllReadButton } from "web_myplann/NotificationCenter/Components/MarkAllReadButton";
import { NotificationCenterError } from "web_myplann/NotificationCenter/Components/NotificationCenterError";
import { RecentGroupSection } from "web_myplann/NotificationCenter/Components/RecentGroupSection";
import { TimelineSkeleton } from "web_myplann/NotificationCenter/Components/TimelineSkeleton";
import { useMarkDisplayedAsRead } from "web_myplann/NotificationCenter/Hooks/useMarkDisplayedAsRead";
import { useNavigateFromTimelineItem } from "web_myplann/NotificationCenter/Hooks/useNavigateFromTimelineItem";
import { useRecentTimelineGroups } from "web_myplann/NotificationCenter/Hooks/useRecentTimelineGroups";
import { EmptyTimelineVariant } from "web_myplann/NotificationCenter/Types/EmptyTimelineVariant";
import { NOTIFICATION_CENTER_ORIGIN } from "web_myplann/NotificationCenter/Utils/notificationOrigin";

const SENTINEL_ROOT_MARGIN = "200px";

export const RecentTab: FC = () => {
	const theme = useTheme();
	const { t } = useResource();
	const { query, groups } = useRecentTimelineGroups();
	const navigateToItem = useNavigateFromTimelineItem({
		origin: NOTIFICATION_CENTER_ORIGIN,
	});
	const sentinelRef = useRef<HTMLDivElement>(null);

	const displayedItems = useMemo(
		() => groups.flatMap((group) => group.items),
		[groups],
	);
	const { hasUnread, markAll, isPending } =
		useMarkDisplayedAsRead(displayedItems);

	const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
					void fetchNextPage();
				}
			},
			{ threshold: 0, rootMargin: SENTINEL_ROOT_MARGIN },
		);

		observer.observe(sentinel);

		return () => {
			observer.disconnect();
		};
	}, [fetchNextPage, hasNextPage, isFetchingNextPage]);

	if (query.isLoading && !query.data) {
		return (
			<TimelineSkeleton
				description={t(Resources.NotificationCenter.Tabs.Recent)}
			/>
		);
	}

	if (query.isError) {
		return (
			<NotificationCenterError
				testId="notification-center-recent-error"
				onRetry={() => {
					void query.refetch();
				}}
			/>
		);
	}

	if (groups.length === 0) {
		return <EmptyTimelineState variant={EmptyTimelineVariant.Recent} />;
	}

	return (
		<Stack gap={0} data-testid="notification-center-recent-content">
			{hasUnread ? (
				<MarkAllReadButton onClick={markAll} disabled={isPending} />
			) : null}
			{groups.map((group) => (
				<RecentGroupSection
					key={group.key}
					group={group}
					onItemClick={navigateToItem}
				/>
			))}
			<Stack
				ref={sentinelRef}
				data-testid="notification-center-recent-sentinel"
				height={1}
			/>
			{isFetchingNextPage ? (
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="center"
					gap={1}
					py={1.6}
					data-testid="notification-center-recent-loading-more"
				>
					<CircularProgress size={20} />
					<Typography variant="caption" color={theme.palette.grey[600]}>
						{t(Resources.NotificationCenter.Common.LoadingMore)}
					</Typography>
				</Stack>
			) : null}
		</Stack>
	);
};
